// BXYZ:..:eliot@bosmanxyz.xyz:..:.www.bosmanxyz.xyz
// Interactive Form with GSAP + Lenis Integration

class BXYZInteractiveForm {
  constructor(formElement) {
    this.form = formElement;
    this.container = formElement.closest('.bxyz-interactive-form');
    this.steps = Array.from(formElement.querySelectorAll('.bxyz-form-step'));
    this.currentStep = 0;
    this.totalSteps = this.steps.length;
    
    // Settings from data attributes
    this.stepMode = this.container.dataset.stepMode === 'true';
    this.animationDuration = parseInt(this.container.dataset.animationDuration) / 1000 || 0.6;
    this.animationEasing = this.container.dataset.animationEasing || 'power2.out';
    this.formular = this.container.dataset.formular || 'projekt';
    this.arAterkallelse = this.formular === 'aterkallelse';
    this.lagringsnyckel = `bxyz-form-data-${this.formular}`;
    
    // Form state
    this.formData = {};
    this.isValid = false;
    
    // Elements
    this.prevBtn = formElement.querySelector('[data-action="prev"]');
    this.nextBtn = formElement.querySelector('[data-action="next"]');
    this.submitBtn = formElement.querySelector('[data-action="submit"]');
    this.progressBar = formElement.querySelector('.bxyz-form-progress__bar');
    this.progressFill = formElement.querySelector('[data-progress-fill]');
    this.progressCurrent = formElement.querySelector('.bxyz-form-progress__current');
    this.progressTotal = formElement.querySelector('.bxyz-form-progress__total');
    this.successMessage = formElement.querySelector('[data-falt="bekraftelse"]');
    this.faltYta = formElement.querySelector('.bxyz-form-falt');
    this.felYta = formElement.querySelector('[data-falt="fel"]');
    
    this.init();
  }
  
  init() {
    // Set total steps
    if (this.progressTotal) {
      this.progressTotal.textContent = this.totalSteps;
    }
    
    // Initialize GSAP ScrollTrigger for viewport animations
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      this.initScrollAnimations();
    }
    
    // Setup step mode or show all
    if (this.stepMode) {
      this.initStepMode();
    } else {
      this.showAllSteps();
    }
    
    // Event listeners
    this.attachEventListeners();
    
    // Auto-save to localStorage
    this.loadSavedData();
    this.enableAutoSave();
    if (this.form.querySelector('[data-post-ok]')) {
      this.spelaSkickad();
    }
  }
  
  initScrollAnimations() {
    // Animate form container on scroll
    gsap.from(this.container, {
      opacity: 0,
      y: 50,
      duration: this.animationDuration * 1.5,
      ease: this.animationEasing,
      scrollTrigger: {
        trigger: this.container,
        start: 'top 80%',
        toggleActions: 'play none none none'
      }
    });
  }
  
  initStepMode() {
    // Hide all steps except first
    this.steps.forEach((step, index) => {
      if (index === 0) {
        step.style.display = 'block';
        step.style.opacity = '1';
        step.style.visibility = 'visible';
        step.classList.add('active');
        this.animateStepIn(step);
      } else {
        step.style.display = 'none';
        step.classList.remove('active');
      }
    });
    
    this.updateControls();
    this.updateProgress();
  }
  
  showAllSteps() {
    // Show all steps at once with stagger
    this.steps.forEach((step, index) => {
      step.classList.add('active');
      step.style.position = 'relative';
      step.style.visibility = 'visible';
      
      if (typeof gsap !== 'undefined') {
        gsap.from(step, {
          opacity: 0,
          x: -30,
          duration: this.animationDuration,
          delay: index * 0.1,
          ease: this.animationEasing
        });
      } else {
        step.style.opacity = '1';
      }
    });
    
    // Hide step controls
    if (this.prevBtn) this.prevBtn.style.display = 'none';
    if (this.nextBtn) this.nextBtn.style.display = 'none';
    if (this.submitBtn) this.submitBtn.style.display = 'block';
    if (this.progressBar) this.progressBar.parentElement.style.display = 'none';
  }
  
  attachEventListeners() {
    // Navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.previousStep());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextStep());
    }
    
    // Form submission
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    
    // Input events - handle multiple inputs per step
    this.steps.forEach(step => {
      const inputs = step.querySelectorAll('.bxyz-form-input');
      inputs.forEach(input => {
        // Focus animations
        input.addEventListener('focus', (e) => this.handleInputFocus(e));
        input.addEventListener('blur', (e) => this.handleInputBlur(e));
        
        // Real-time validation
        input.addEventListener('input', (e) => this.handleInputChange(e));
        
        // Keyboard navigation
        input.addEventListener('keydown', (e) => this.handleKeydown(e));
      });
    });
  }
  
  handleInputFocus(e) {
    const input = e.target;
    const label = input.previousElementSibling;
    
    if (typeof gsap !== 'undefined') {
      gsap.to(input, {
        scale: 1.02,
        duration: 0.2,
        ease: 'power2.out'
      });
      
      if (label) {
        gsap.to(label, {
          color: this.container.dataset.accentColor || '#808080',
          duration: 0.3,
          ease: 'power2.out'
        });
      }
    }
  }
  
  handleInputBlur(e) {
    const input = e.target;
    const label = input.previousElementSibling;
    
    if (typeof gsap !== 'undefined') {
      gsap.to(input, {
        scale: 1,
        duration: 0.2,
        ease: 'power2.out'
      });
      
      if (label) {
        gsap.to(label, {
          color: this.container.dataset.textColor || '#FFFFFF',
          duration: 0.3,
          ease: 'power2.out'
        });
      }
    }
    
    this.validateField(input);
  }
  
  handleInputChange(e) {
    const input = e.target;
    const fieldName = input.dataset.field;
    this.formData[fieldName] = input.value;
  }
  
  handleKeydown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (this.stepMode) {
        this.nextStep();
      }
    }
  }
  
  validateField(input) {
    let isValid = true;
    let message = '';
    
    // Check if this specific input is required
    const isRequired = input.hasAttribute('required');
    
    if (isRequired && !input.value.trim()) {
      isValid = false;
      message = 'This field is required';
    } else if (input.type === 'email' && input.value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(input.value)) {
        isValid = false;
        message = 'Please enter a valid email';
      }
    }
    
    return isValid;
  }
  
  validateStep(stepEl) {
    const validation = stepEl.querySelector('.bxyz-form-validation');
    const inputs = stepEl.querySelectorAll('.bxyz-form-input');
    let allValid = true;
    let message = '';
    
    inputs.forEach(input => {
      if (!this.validateField(input)) {
        allValid = false;
        if (input.type === 'email') {
          message = 'Please enter a valid email';
        } else if (input.hasAttribute('required')) {
          message = 'This field is required';
        }
      }
    });
    
    if (validation) {
      validation.textContent = message;
      
      if (typeof gsap !== 'undefined' && message) {
        gsap.from(validation, {
          opacity: 0,
          y: -10,
          duration: 0.3,
          ease: 'power2.out'
        });
      }
    }
    
    return allValid;
  }
  
  nextStep() {
    if (this.currentStep >= this.totalSteps - 1) return;
    
    // Validate current step (all inputs in step)
    const currentStepEl = this.steps[this.currentStep];
    
    if (!this.validateStep(currentStepEl)) {
      const firstInput = currentStepEl.querySelector('.bxyz-form-input');
      if (firstInput) this.shakeInput(firstInput);
      return;
    }
    
    // Animate out current step
    this.animateStepOut(currentStepEl, () => {
      currentStepEl.classList.remove('active');
      currentStepEl.style.display = 'none';
      
      // Move to next step
      this.currentStep++;
      const nextStepEl = this.steps[this.currentStep];
      if (nextStepEl.dataset.step === 'bekrafta') {
        this.fyllSammanfattning();
      }
      nextStepEl.style.display = 'block';
      
      // Animate in next step
      setTimeout(() => {
        nextStepEl.classList.add('active');
        this.animateStepIn(nextStepEl);
        
        // Focus input
        const nextInput = nextStepEl.querySelector('input.bxyz-form-input, textarea.bxyz-form-input');
        if (nextInput) {
          setTimeout(() => nextInput.focus(), this.animationDuration * 500);
        }
      }, 50);
      
      this.updateControls();
      this.updateProgress();
      
      // Scroll to form if needed
      if (window.lenis) {
        window.lenis.scrollTo(this.container, {
          offset: -100,
          duration: 1
        });
      }
    });
  }
  
  previousStep() {
    if (this.currentStep <= 0) return;
    
    const currentStepEl = this.steps[this.currentStep];
    
    this.animateStepOut(currentStepEl, () => {
      currentStepEl.classList.remove('active');
      currentStepEl.style.display = 'none';
      
      this.currentStep--;
      const prevStepEl = this.steps[this.currentStep];
      prevStepEl.style.display = 'block';
      
      setTimeout(() => {
        prevStepEl.classList.add('active');
        this.animateStepIn(prevStepEl);
        
        const prevInput = prevStepEl.querySelector('input.bxyz-form-input, textarea.bxyz-form-input');
        if (prevInput) {
          setTimeout(() => prevInput.focus(), this.animationDuration * 500);
        }
      }, 50);
      
      this.updateControls();
      this.updateProgress();
    });
  }
  
  animateStepIn(step) {
    // Ensure step is visible
    step.style.display = 'block';
    step.style.visibility = 'visible';
    
    if (typeof gsap === 'undefined') {
      step.style.opacity = '1';
      return;
    }
    
    const label = step.querySelector('.bxyz-form-label');
    const input = step.querySelector('.bxyz-form-input');
    
    gsap.fromTo(step, 
      { opacity: 0, x: 50 },
      { 
        opacity: 1, 
        x: 0, 
        duration: this.animationDuration,
        ease: this.animationEasing
      }
    );
    
    if (label) {
      gsap.from(label, {
        opacity: 0,
        y: -20,
        duration: this.animationDuration * 0.8,
        delay: this.animationDuration * 0.2,
        ease: this.animationEasing
      });
    }
    
    if (input) {
      gsap.from(input, {
        opacity: 0,
        scaleX: 0.8,
        duration: this.animationDuration * 0.8,
        delay: this.animationDuration * 0.3,
        ease: this.animationEasing,
        transformOrigin: 'left center'
      });
    }
  }
  
  animateStepOut(step, callback) {
    if (typeof gsap === 'undefined') {
      step.style.opacity = '0';
      if (callback) callback();
      return;
    }
    
    gsap.to(step, {
      opacity: 0,
      x: -50,
      duration: this.animationDuration * 0.7,
      ease: 'power2.in',
      onComplete: callback
    });
  }
  
  shakeInput(input) {
    if (typeof gsap === 'undefined') return;
    
    gsap.to(input, {
      x: [-10, 10, -10, 10, 0],
      duration: 0.4,
      ease: 'power2.out'
    });
  }
  
  updateControls() {
    if (!this.stepMode) return;
    
    // Previous button
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentStep === 0;
      this.prevBtn.style.display = this.currentStep === 0 ? 'none' : 'block';
    }
    
    // Next/Submit button
    const isLastStep = this.currentStep === this.totalSteps - 1;
    
    if (this.nextBtn) {
      this.nextBtn.style.display = isLastStep ? 'none' : 'block';
    }
    
    if (this.submitBtn) {
      this.submitBtn.style.display = isLastStep ? 'block' : 'none';
    }
  }
  
  updateProgress() {
    const progress = ((this.currentStep + 1) / this.totalSteps) * 100;
    
    if (this.progressFill) {
      if (typeof gsap !== 'undefined') {
        gsap.to(this.progressFill, {
          width: `${progress}%`,
          duration: 0.5,
          ease: 'power2.out'
        });
      } else {
        this.progressFill.style.width = `${progress}%`;
      }
    }
    
    if (this.progressCurrent) {
      this.progressCurrent.textContent = this.currentStep + 1;
    }
  }
  
  handleSubmit(e) {
    e.preventDefault();

    let allValid = true;
    this.steps.forEach(step => {
      if (!this.validateStep(step)) {
        allValid = false;
      }
    });

    if (!allValid) {
      this.shakeInput(this.form);
      return;
    }

    if (this.arAterkallelse) {
      this.skrivKropp();
    }

    this.skickaKontakt();
  }

  visaFel(meddelande) {
    this.container.dataset.post = 'fel';
    if (!this.felYta) return;
    this.felYta.hidden = false;
    this.felYta.textContent = meddelande;
  }

  doljFel() {
    if (this.container.dataset.post === 'fel') {
      delete this.container.dataset.post;
    }
    if (!this.felYta) return;
    this.felYta.hidden = true;
    this.felYta.textContent = '';
  }

  atestallFalt() {
    this.form.querySelectorAll('.bxyz-form-input').forEach((el) => {
      if (el.tagName === 'DD') {
        el.textContent = '';
        return;
      }
      el.value = '';
    });
    const kropp = this.form.querySelector('[data-field="kropp"]');
    if (kropp) kropp.value = 'WITHDRAWAL NOTICE';
    const amne = this.form.querySelector('[data-field="amne"]');
    if (amne) amne.value = 'Withdrawal notice';
    this.formData = {};
    this.currentStep = 0;
    if (this.stepMode) {
      this.initStepMode();
    }
  }

  spelaSkickad() {
    this.clearSavedData();
    this.doljFel();
    const falt = this.faltYta || this.form;
    const hold = 1;
    const visaFaltIgen = () => {
      this.atestallFalt();
      delete this.container.dataset.post;
      if (typeof gsap !== 'undefined') {
        gsap.fromTo(
          falt,
          { opacity: 0, y: 24 },
          { opacity: 1, y: 0, duration: this.animationDuration, ease: this.animationEasing }
        );
      } else {
        falt.style.opacity = '1';
        falt.style.transform = '';
      }
      if (this.submitBtn) this.submitBtn.disabled = false;
    };

    const efterHall = () => {
      if (typeof gsap !== 'undefined' && this.successMessage) {
        gsap.to(this.successMessage, {
          opacity: 0,
          duration: this.animationDuration / 2,
          ease: this.animationEasing,
          onComplete: visaFaltIgen
        });
      } else {
        visaFaltIgen();
      }
    };

    if (typeof gsap !== 'undefined') {
      gsap.to(falt, {
        opacity: 0,
        y: -24,
        duration: this.animationDuration,
        ease: this.animationEasing,
        onComplete: () => {
          this.container.dataset.post = 'skickad';
          if (this.successMessage) {
            gsap.fromTo(
              this.successMessage,
              { opacity: 0, y: 12 },
              { opacity: 1, y: 0, duration: this.animationDuration, ease: this.animationEasing }
            );
          }
          gsap.delayedCall(hold, efterHall);
        }
      });
    } else {
      falt.style.opacity = '0';
      this.container.dataset.post = 'skickad';
      setTimeout(efterHall, hold * 1000);
    }
  }

  async lasFel(response) {
    const fallback = 'The notice was not sent. Try again.';
    try {
      const data = await response.json();
      const delar = Object.values(data || {}).flat().filter(Boolean);
      if (delar.length) return delar.join(' ');
    } catch (err) {
      return fallback;
    }
    return fallback;
  }

  skickaKontakt() {
    this.doljFel();
    if (this.submitBtn) this.submitBtn.disabled = true;
    const formData = new FormData(this.form);

    fetch(this.form.action, {
      method: 'POST',
      body: formData,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).then(async (response) => {
      const url = response.url || '';
      if (url.includes('contact_posted=true') && response.ok) {
        this.spelaSkickad();
        return;
      }
      if (response.status === 422 || response.status === 400 || response.status === 429) {
        this.visaFel(await this.lasFel(response));
        if (this.submitBtn) this.submitBtn.disabled = false;
        return;
      }
      if (!response.ok) {
        this.visaFel(await this.lasFel(response));
        if (this.submitBtn) this.submitBtn.disabled = false;
        return;
      }
      const typ = response.headers.get('content-type') || '';
      if (typ.includes('json')) {
        const data = await response.json().catch(() => ({}));
        const nycklar = Object.keys(data || {});
        const felNycklar = nycklar.filter((k) => k !== 'posted_successfully' && k !== 'status');
        if (felNycklar.length && data.posted_successfully !== true) {
          const delar = felNycklar.flatMap((k) => data[k]).filter(Boolean);
          this.visaFel(delar.length ? delar.join(' ') : 'The notice was not sent. Try again.');
          if (this.submitBtn) this.submitBtn.disabled = false;
          return;
        }
      }
      this.spelaSkickad();
    }).catch(() => {
      this.visaFel('The notice was not sent. Try again.');
      if (this.submitBtn) this.submitBtn.disabled = false;
    });
  }
  
  faltVarde(namn) {
    const el = this.form.querySelector(`[data-field="${namn}"]`);
    if (!el) return '';
    return 'value' in el ? String(el.value).trim() : '';
  }

  fyllSammanfattning() {
    const set = (namn, varde) => {
      const el = this.form.querySelector(`[data-falt="${namn}"]`);
      if (el) el.textContent = varde;
    };
    set('sammanfattning-namn', this.faltVarde('name'));
    set('sammanfattning-epost', this.faltVarde('email'));
    set('sammanfattning-order', this.faltVarde('order'));
  }

  skrivKropp() {
    const namn = this.faltVarde('name');
    const epost = this.faltVarde('email');
    const order = this.faltVarde('order');
    const detaljer = this.faltVarde('detaljer');
    const rader = [
      'WITHDRAWAL NOTICE',
      `Name: ${namn}`,
      `Email: ${epost}`,
      `Order number: ${order}`,
    ];
    if (detaljer) rader.push(`Details: ${detaljer}`);
    const kropp = this.form.querySelector('[data-field="kropp"]');
    if (kropp) kropp.value = rader.join('\n');
    const amne = this.form.querySelector('[data-field="amne"]');
    if (amne) {
      const bas = amne.getAttribute('value') || 'Withdrawal notice';
      amne.value = order ? `${bas} — ${order}` : bas;
    }
  }

  // Auto-save functionality
  enableAutoSave() {
    this.form.addEventListener('input', () => {
      localStorage.setItem(this.lagringsnyckel, JSON.stringify(this.formData));
    });
  }
  
  loadSavedData() {
    const saved = localStorage.getItem(this.lagringsnyckel);
    if (saved) {
      try {
        this.formData = JSON.parse(saved);
        Object.keys(this.formData).forEach(key => {
          const input = this.form.querySelector(`[data-field="${key}"]`);
          if (input) {
            input.value = this.formData[key];
          }
        });
      } catch (e) {
        console.error('Failed to load saved form data', e);
      }
    }
  }
  
  clearSavedData() {
    localStorage.removeItem(this.lagringsnyckel);
    this.formData = {};
  }
}

// Initialize all forms
document.addEventListener('DOMContentLoaded', () => {
  const forms = document.querySelectorAll('.bxyz-interactive-form__form');
  forms.forEach(form => {
    new BXYZInteractiveForm(form);
  });
});
